import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { Navigate } from "react-router-dom";
import LocationTree from "../components/LocationTree";
import ErrorModal from "../components/ErrorModal";
import { formatNumber } from "../utils/format";
import { exportLocationLabelsToPDF } from "../utils/locationLabelsPdf";
import { exportProductLabelsToPDF } from "../utils/productLabelsPdf";

import { usePermission } from "../hooks/usePermission";

export default function LocationList({ user }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("locations.create");
  const canEdit = hasPermission("locations.edit");
  const canDelete = hasPermission("locations.delete");
  const [tree, setTree] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [unassignedProducts, setUnassignedProducts] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const fileInputRef = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportingLabels, setExportingLabels] = useState(false);
  const [exportLeafOnly, setExportLeafOnly] = useState(true);
  const [showProductStickersModal, setShowProductStickersModal] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState({});
  const [exportingProductStickers, setExportingProductStickers] = useState(false);

  const fetchTree = () => {
    setLoading(true);
    apiFetch("/locations/tree")
      .then(setTree)
      .catch((err) => {
        console.error("Fout bij ophalen locaties:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  // Helper functions for optimistic tree updates
  const addNodeToTree = (nodes, parentId, newNode) => {
    if (parentId === null) {
      return [...nodes, { ...newNode, children: [] }];
    }
    return nodes.map(node => {
      if (node.id === parentId) {
        return { ...node, children: [...(node.children || []), { ...newNode, children: [] }] };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: addNodeToTree(node.children, parentId, newNode) };
      }
      return node;
    });
  };

  const updateNodeInTree = (nodes, nodeId, updates) => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, ...updates };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: updateNodeInTree(node.children, nodeId, updates) };
      }
      return node;
    });
  };

  const removeNodeFromTree = (nodes, nodeId) => {
    return nodes
      .filter(node => node.id !== nodeId)
      .map(node => {
        if (node.children && node.children.length > 0) {
          return { ...node, children: removeNodeFromTree(node.children, nodeId) };
        }
        return node;
      });
  };

  const fetchUnassignedProducts = () => {
    apiFetch("/locations/unassigned/products")
      .then(setUnassignedProducts)
      .catch((err) => {
        console.error("Fout bij ophalen producten zonder locatie:", err);
      });
  };

  const fetchLocationDetails = (locationId) => {
    apiFetch(`/locations/${locationId}`)
      .then(setSelectedLocation)
      .catch((err) => {
        console.error("Fout bij ophalen locatie details:", err);
        setErrorModal({ show: true, message: "Kon locatie details niet laden." });
      });
  };

  useEffect(() => {
    if (!user) return;
    fetchTree();
    fetchUnassignedProducts();
  }, [user]);

  const handleSelect = (node) => {
    fetchLocationDetails(node.id);
  };

  const handleExportLabels = async () => {
    setExportingLabels(true);
    try {
      const locations = await apiFetch('/locations');
      await exportLocationLabelsToPDF(locations, { leafOnly: exportLeafOnly });
      setShowExportModal(false);
    } catch (err) {
      console.error('Error exporting location labels:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij exporteren van locatielabels' });
    } finally {
      setExportingLabels(false);
    }
  };

  const handleOpenProductStickersModal = () => {
    // Get all unique brands from products at this location
    const products = selectedLocation?.products || [];
    console.log('[ProductStickers] Opening modal, products:', products.length);
    const brands = [...new Set(products.map(p => p.brand || 'Zonder merk'))].sort((a, b) => {
      if (a === 'Zonder merk') return 1;
      if (b === 'Zonder merk') return -1;
      return a.localeCompare(b);
    });
    console.log('[ProductStickers] Brands found:', brands);
    // Select all brands by default
    const initialSelection = {};
    brands.forEach(brand => {
      initialSelection[brand] = true;
    });
    setSelectedBrands(initialSelection);
    setShowProductStickersModal(true);
  };

  const handleExportProductStickers = async () => {
    setExportingProductStickers(true);
    try {
      const products = selectedLocation?.products || [];
      // Filter by selected brands
      const filteredProducts = products.filter(p => {
        const brand = p.brand || 'Zonder merk';
        return selectedBrands[brand];
      });
      if (filteredProducts.length === 0) {
        setErrorModal({ show: true, message: 'Geen producten geselecteerd. Selecteer minstens 1 merk.' });
        return;
      }
      await exportProductLabelsToPDF(filteredProducts, { locationName: selectedLocation?.name });
      setShowProductStickersModal(false);
    } catch (err) {
      console.error('Error exporting product stickers:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij exporteren van productstickers' });
    } finally {
      setExportingProductStickers(false);
    }
  };

  const handleAddRoot = () => {
    setEditingLocation(null);
    setParentForNew(null);
    setFormData({ name: "", description: "" });
    setShowModal(true);
  };

  const handleAddChild = (parentNode) => {
    setEditingLocation(null);
    setParentForNew(parentNode);
    setFormData({ name: "", description: "" });
    setShowModal(true);
  };

  const handleEdit = (node) => {
    setEditingLocation(node);
    setParentForNew(null);
    setFormData({ name: node.name, description: node.description || "" });
    setShowModal(true);
  };

  const handleDelete = (node) => {
    setDeletingLocation(node);
    setShowDeleteModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormLoading(true);

    if (editingLocation) {
      // Update existing location
      apiFetch(`/locations/${editingLocation.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
        }),
      })
        .then((updatedLocation) => {
          setShowModal(false);
          // Optimistic update: update node in tree
          setTree(prev => updateNodeInTree(prev, editingLocation.id, {
            name: formData.name,
            description: formData.description || null,
          }));
          if (selectedLocation && selectedLocation.id === editingLocation.id) {
            setSelectedLocation(prev => ({
              ...prev,
              name: formData.name,
              description: formData.description || null,
            }));
          }
        })
        .catch((err) => {
          console.error("Fout bij bewerken locatie:", err);
          setErrorModal({ show: true, message: "Kon locatie niet bewerken." });
        })
        .finally(() => setFormLoading(false));
    } else {
      // Create new location
      apiFetch("/locations", {
        method: "POST",
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          parent_id: parentForNew ? parentForNew.id : null,
        }),
      })
        .then((newLocation) => {
          setShowModal(false);
          // Optimistic update: add node to tree
          setTree(prev => addNodeToTree(prev, parentForNew?.id || null, newLocation));
          fetchLocationDetails(newLocation.id);
        })
        .catch((err) => {
          console.error("Fout bij aanmaken locatie:", err);
          setErrorModal({ show: true, message: "Kon locatie niet aanmaken." });
        })
        .finally(() => setFormLoading(false));
    }
  };

  const confirmDelete = () => {
    if (!deletingLocation) return;

    setFormLoading(true);
    apiFetch(`/locations/${deletingLocation.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        const deletedId = deletingLocation.id;
        setDeletingLocation(null);
        if (selectedLocation && selectedLocation.id === deletedId) {
          setSelectedLocation(null);
        }
        // Optimistic update: remove node from tree
        setTree(prev => removeNodeFromTree(prev, deletedId));
        fetchUnassignedProducts();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen locatie:", err);
        const message = err.message.includes("onderliggende")
          ? err.message
          : "Kon locatie niet verwijderen.";
        setErrorModal({ show: true, message });
      })
      .finally(() => setFormLoading(false));
  };

  const handleDropProduct = (productId, locationId, productName, locationName) => {
    apiFetch(`/locations/${locationId}/products/${productId}`, { method: "POST" })
      .then(() => {
        // Refresh the selected location's product list if viewing that location
        if (selectedLocation && selectedLocation.id === locationId) {
          fetchLocationDetails(locationId);
        }
        // Refresh unassigned products
        fetchUnassignedProducts();
      })
      .catch((err) => {
        console.error("Fout bij toewijzen product:", err);
        setErrorModal({
          show: true,
          message: `Kon "${productName}" niet toewijzen aan "${locationName}".`,
        });
      });
  };

  const handleRemoveProduct = (productId, locationId, productName) => {
    apiFetch(`/locations/${locationId}/products/${productId}`, { method: "DELETE" })
      .then(() => {
        fetchLocationDetails(locationId);
        fetchUnassignedProducts();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen product van locatie:", err);
        setErrorModal({
          show: true,
          message: `Kon "${productName}" niet verwijderen van locatie.`,
        });
      });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLocation) return;

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);

    // Get access token from localStorage (same as apiFetch)
    const getAccessToken = () => {
      try {
        const key = `sb-${process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`;
        const item = localStorage.getItem(key);
        if (!item) return null;
        return JSON.parse(item)?.access_token;
      } catch {
        return null;
      }
    };

    try {
      const accessToken = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || ""}/locations/${selectedLocation.id}/photo`,
        {
          method: "POST",
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Upload mislukt");
      }

      fetchLocationDetails(selectedLocation.id);
    } catch (err) {
      console.error("Fout bij uploaden foto:", err);
      setErrorModal({ show: true, message: "Kon foto niet uploaden." });
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handlePhotoDelete = () => {
    if (!selectedLocation) return;

    apiFetch(`/locations/${selectedLocation.id}/photo`, { method: "DELETE" })
      .then(() => {
        fetchLocationDetails(selectedLocation.id);
      })
      .catch((err) => {
        console.error("Fout bij verwijderen foto:", err);
        setErrorModal({ show: true, message: "Kon foto niet verwijderen." });
      });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedProducts = (products) => {
    if (!sortConfig.key || !products) return products;

    return [...products].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // String comparison for text fields
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === 'asc' ? "↑" : "↓";
  };

  const SortHeader = ({ label, sortKey, className = "" }) => (
    <th
      className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      {label} {getSortIcon(sortKey)}
    </th>
  );

  if (unauthorized) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="p-6 h-[calc(100vh-4rem)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Locaties</h1>
        <button
          onClick={() => setShowExportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          Exporteer Labels
        </button>
      </div>

      <div className="flex gap-6 h-[calc(100%-4rem)]">
        {/* Left Panel - Tree View */}
        <div className="w-1/3 bg-white rounded-lg shadow overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto p-2">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <LocationTree
                tree={tree.filter(node => node.name !== 'Onbestemd')}
                selectedId={selectedLocation?.id}
                onSelect={handleSelect}
                onAddChild={canCreate ? handleAddChild : null}
                onEdit={canEdit ? handleEdit : null}
                onDelete={canDelete ? handleDelete : null}
                onAddRoot={canCreate ? handleAddRoot : null}
                onDropProduct={canEdit ? handleDropProduct : null}
              />
            )}
          </div>

          {/* Unassigned Products Section */}
          <div className="border-t">
            <button
              onClick={() => setSelectedLocation({ id: null, name: "Onbestemd", is_special: true })}
              className={`w-full p-3 text-left flex items-center gap-2 hover:bg-gray-50 ${
                selectedLocation?.is_special ? "bg-orange-50" : ""
              }`}
            >
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium text-gray-700">Onbestemd</span>
              <span className="ml-auto text-sm text-gray-500">
                {unassignedProducts.length} product(en)
              </span>
            </button>
          </div>
        </div>

        {/* Right Panel - Details */}
        <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col">
          {selectedLocation ? (
            <>
              {/* Header */}
              <div className="p-6 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedLocation.name}
                    </h2>
                    {selectedLocation.code && (
                      <p className="text-sm text-gray-500 mt-1">
                        Code: {selectedLocation.code}
                      </p>
                    )}
                  </div>
                  {!selectedLocation.is_special && (
                    <div className="flex gap-2">
                      {selectedLocation.products && selectedLocation.products.length > 0 && (
                        <button
                          onClick={handleOpenProductStickersModal}
                          className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 border border-blue-600 rounded hover:bg-blue-50 flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          Stickers
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(selectedLocation)}
                          className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 border border-blue-600 rounded hover:bg-blue-50"
                        >
                          Bewerken
                        </button>
                      )}
                      {canDelete && !selectedLocation.is_root && (
                        <button
                          onClick={() => handleDelete(selectedLocation)}
                          className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 border border-red-600 rounded hover:bg-red-50"
                        >
                          Verwijderen
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Description */}
                {selectedLocation.description && (
                  <p className="mt-4 text-gray-600">{selectedLocation.description}</p>
                )}

                {/* Photo Section */}
                {!selectedLocation.is_special && (
                  <div className="mt-4">
                    {selectedLocation.photo_url ? (
                      <div className="relative inline-block">
                        <img
                          src={selectedLocation.photo_url}
                          alt={selectedLocation.name}
                          className="max-w-xs max-h-48 rounded-lg shadow-sm"
                        />
                        <button
                          onClick={handlePhotoDelete}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          title="Foto verwijderen"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          id="photo-upload"
                        />
                        <label
                          htmlFor="photo-upload"
                          className={`cursor-pointer px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2 ${
                            uploadingPhoto ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          {uploadingPhoto ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              Uploaden...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Foto toevoegen
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Products List */}
              <div className="flex-1 overflow-auto p-6">
                <h3 className="font-semibold text-gray-700 mb-4">
                  Producten op deze locatie
                </h3>
                {(selectedLocation.is_special ? unassignedProducts : selectedLocation.products || []).length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    Geen producten op deze locatie.
                  </p>
                ) : (
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortHeader label="Merk" sortKey="brand" className="text-left" />
                        <SortHeader label="Product" sortKey="product_name" className="text-left" />
                        <SortHeader label="Bevestigd" sortKey="confirmed_stock" className="text-right" />
                        <SortHeader label="Geschat" sortKey="estimated_stock" className="text-right" />
                        {canEdit && !selectedLocation.is_special && (
                          <th className="w-10"></th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getSortedProducts(selectedLocation.is_special ? unassignedProducts : selectedLocation.products || []).map((product) => (
                        <tr
                          key={product.id}
                          className={`hover:bg-gray-50 ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
                          onClick={() => navigate(`/products/${product.id}`)}
                          draggable={canEdit}
                          onDragStart={(e) => {
                            e.dataTransfer.setData("application/json", JSON.stringify({
                              productId: product.id,
                              productName: product.product_name,
                              fromLocationId: selectedLocation?.id || null,
                            }));
                            e.dataTransfer.effectAllowed = "copy";
                          }}
                        >
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {product.brand}
                          </td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">
                            {product.product_name}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-600">
                            {formatNumber(product.confirmed_stock, 0)}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-600">
                            {formatNumber(product.estimated_stock, 0)}
                          </td>
                          {canEdit && !selectedLocation.is_special && (
                            <td className="px-2 py-2 text-right">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveProduct(product.id, selectedLocation.id, product.product_name);
                                }}
                                className="p-1 text-red-400 hover:text-red-600 rounded"
                                title="Verwijder van deze locatie"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p>Selecteer een locatie om de details te bekijken</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingLocation
                ? "Locatie Bewerken"
                : parentForNew
                ? `Sublocatie toevoegen aan "${parentForNew.name}"`
                : "Nieuwe Hoofdlocatie"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naam *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="bijv. Bona Derma, Kabinet 1, Kast A..."
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Omschrijving
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Optionele omschrijving van de locatie..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={formLoading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={formLoading}
                >
                  {formLoading ? "Bezig..." : "Opslaan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Locatie Verwijderen</h2>
            <p className="mb-4 text-gray-700">
              Weet je zeker dat je de locatie "<strong>{deletingLocation.name}</strong>" wilt verwijderen?
            </p>
            <p className="mb-6 text-sm text-orange-600 bg-orange-50 p-3 rounded">
              Producten op deze locatie zullen worden verplaatst naar "Onbestemd".
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingLocation(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={formLoading}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={formLoading}
              >
                {formLoading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Labels Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Locatielabels Exporteren</h2>
            <p className="text-gray-600 mb-4">
              Genereer een PDF met QR codes en codes voor alle locaties.
              De labels zijn ontworpen om geprint te worden en op schappen geplakt te worden.
            </p>

            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <input
                  type="radio"
                  name="exportType"
                  checked={exportLeafOnly}
                  onChange={() => setExportLeafOnly(true)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900">Enkel laagste niveau</p>
                  <p className="text-sm text-gray-500">Alleen locaties zonder sublocaties (aanbevolen)</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 mt-2">
                <input
                  type="radio"
                  name="exportType"
                  checked={!exportLeafOnly}
                  onChange={() => setExportLeafOnly(false)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900">Alle locaties</p>
                  <p className="text-sm text-gray-500">Inclusief hoofdlocaties en tussenliggende niveaus</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={exportingLabels}
              >
                Annuleren
              </button>
              <button
                onClick={handleExportLabels}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={exportingLabels}
              >
                {exportingLabels ? 'Bezig met exporteren...' : 'Exporteren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Stickers Modal */}
      {showProductStickersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
            <h2 className="text-xl font-bold mb-2">Productstickers Exporteren</h2>
            <p className="text-sm text-gray-600 mb-4">
              {selectedLocation?.name} - {selectedLocation?.products?.length || 0} producten
            </p>

            <p className="text-sm text-gray-700 mb-3">
              Selecteer de merken waarvan je stickers wilt genereren:
            </p>

            <div className="flex-1 overflow-auto border rounded-lg p-2 mb-4">
              {Object.keys(selectedBrands).length === 0 ? (
                <p className="text-gray-500 text-sm p-2">Geen producten op deze locatie.</p>
              ) : (
                <>
                  <div className="flex gap-2 mb-2 pb-2 border-b">
                    <button
                      onClick={() => {
                        const allSelected = {};
                        Object.keys(selectedBrands).forEach(b => { allSelected[b] = true; });
                        setSelectedBrands(allSelected);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Alles selecteren
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => {
                        const noneSelected = {};
                        Object.keys(selectedBrands).forEach(b => { noneSelected[b] = false; });
                        setSelectedBrands(noneSelected);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Niets selecteren
                    </button>
                  </div>
                  {Object.keys(selectedBrands).sort((a, b) => {
                    if (a === 'Zonder merk') return 1;
                    if (b === 'Zonder merk') return -1;
                    return a.localeCompare(b);
                  }).map(brand => {
                    const count = (selectedLocation?.products || []).filter(p => (p.brand || 'Zonder merk') === brand).length;
                    return (
                      <label key={brand} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedBrands[brand] || false}
                          onChange={(e) => setSelectedBrands(prev => ({ ...prev, [brand]: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="flex-1">{brand}</span>
                        <span className="text-sm text-gray-500">({count})</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Geselecteerd: {Object.values(selectedBrands).filter(Boolean).length} van {Object.keys(selectedBrands).length} merken
              ({(selectedLocation?.products || []).filter(p => selectedBrands[p.brand || 'Zonder merk']).length} producten)
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowProductStickersModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={exportingProductStickers}
              >
                Annuleren
              </button>
              <button
                onClick={handleExportProductStickers}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={exportingProductStickers || Object.values(selectedBrands).filter(Boolean).length === 0}
              >
                {exportingProductStickers ? 'Bezig...' : 'Exporteren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
